/**
 * MMOPlanet - MMO mode planet entity
 * Displays planets in the persistent MMO world with server-driven state
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord, getPlanetFootprint, getFootprintHexes, ISO_Y_SCALE } from '../../shared/HexUtils';
import type { MMOPlanet as MMOPlanetData } from '../../managers/MMOStateManager';

const PLAYER_COLORS = [
  0x3388ee, 0xee4444, 0x44cc55, 0xeeee44,
  0xf08822, 0xbb55ee, 0x00ffff, 0xff00ff,
];

const NEUTRAL_COLOR = 0x888888;
const HEX_CELL_RADIUS = 42;

export class MMOPlanet extends Phaser.GameObjects.Container {
  public id: string;
  public planetName: string;
  public ownerId: string | null;
  public hexCoord: HexCoord;
  public isHomePlanet: boolean;
  public currentHp: number;
  public maxHp: number;
  public currentShields: number;
  public maxShields: number;
  public buildings: any[];
  public productionQueue: any[];
  public maxStorage: number;

  private planetGraphics: Phaser.GameObjects.Graphics;
  private planetSprite: Phaser.GameObjects.Image | null = null;
  private footprintGraphics: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private ambientTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, data: MMOPlanetData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.planetName = data.name;
    this.ownerId = data.ownerId;
    this.hexCoord = data.position;
    this.isHomePlanet = data.isHomePlanet;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.shields;
    this.maxShields = data.maxShields;
    this.buildings = data.buildings || [];
    this.productionQueue = data.productionQueue || [];
    this.maxStorage = data.maxStorage || 0;

    this.footprintGraphics = this.scene.add.graphics();
    this.add(this.footprintGraphics);

    this.planetGraphics = this.scene.add.graphics();
    this.add(this.planetGraphics);

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    const displayRadius = this.getPlanetDisplayRadius();
    this.nameLabel = this.scene.add.text(0, -(displayRadius + 15), this.planetName, {
      fontSize: '11px',
      color: '#88ddff',
      fontFamily: '"Share Tech Mono", monospace',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();

    this.ambientTween = this.scene.tweens.add({
      targets: this,
      scaleX: { from: 1.0, to: 1.02 },
      scaleY: { from: 1.0, to: 1.02 },
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.setDepth(5);

    const hitboxSize = displayRadius * 2 + 20;
    this.setSize(hitboxSize, hitboxSize);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-hitboxSize / 2, -hitboxSize / 2, hitboxSize, hitboxSize),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler. See MMOUnit.ts for rationale —
    // per-sprite pointerdown fires the instant a finger touches the sprite, before
    // the scene's drag-threshold check runs, so it hijacks intended camera pans.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint()
    // (which still reads entity.input.hitArea, so setInteractive() is required).
  }

  public updateFromServer(data: MMOPlanetData): void {
    if (!this.scene || !this.scene.sys) return;

    this.planetName = data.name;
    this.ownerId = data.ownerId;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.currentShields = data.shields;
    this.maxShields = data.maxShields;
    this.buildings = data.buildings || [];
    this.productionQueue = data.productionQueue || [];
    this.isHomePlanet = data.isHomePlanet;
    this.maxStorage = data.maxStorage || 0;

    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      this.updateVisualPosition();
    }

    this.drawVisuals();
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.4);
    } else {
      this.setAlpha(1);
    }
  }

  private drawVisuals(): void {
    this.drawFootprintHighlight();
    this.drawPlanet();
    this.drawSelectionIndicator();
    this.nameLabel.setText(this.planetName);
  }

  private getPlanetDisplayRadius(): number {
    const footprint = getPlanetFootprint(this.isHomePlanet);
    if (footprint === 7) return 130;
    if (footprint === 3) return 80;
    return 25;
  }

  private getPlanetSpriteKey(): string | null {
    const isEarth = this.hexCoord.q === 0 && this.hexCoord.r === 0;
    if (isEarth) return 'planet-terra';

    const name = this.planetName.toLowerCase().replace(/\s+/g, '-');
    const directKey = `planet-${name}`;
    if (this.scene.textures.exists(directKey)) return directKey;

    const PLANET_POOL = [
      'planet-terra', 'planet-pyraxis', 'planet-virellion', 'planet-elaris',
      'planet-nyxara', 'planet-brasskar', 'planet-geodrys', 'planet-ferronis',
      'planet-tempestris', 'planet-mirepox', 'planet-thalora', 'planet-cryovale',
      'planet-saharrax', 'planet-verdantis',
    ];
    const available = PLANET_POOL.filter(k => this.scene.textures.exists(k));
    if (available.length > 0) {
      let hash = 0;
      for (let i = 0; i < this.id.length; i++) hash = ((hash << 5) - hash + this.id.charCodeAt(i)) | 0;
      return available[Math.abs(hash) % available.length];
    }
    return null;
  }

  private drawFootprintHighlight(): void {
    this.footprintGraphics.clear();
    const color = this.getOwnerColor();
    const footprint = getPlanetFootprint(this.isHomePlanet);
    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      const ox = p.x - anchorPixel.x;
      const oy = p.y - anchorPixel.y;

      this.footprintGraphics.fillStyle(color, 0.08);
      this.drawHexagon(this.footprintGraphics, ox, oy, HEX_CELL_RADIUS, true);
      this.footprintGraphics.lineStyle(1.5, color, 0.25);
      this.drawHexagon(this.footprintGraphics, ox, oy, HEX_CELL_RADIUS, false);
    }
  }

  private drawPlanet(): void {
    this.planetGraphics.clear();

    const displayRadius = this.getPlanetDisplayRadius();
    const spriteKey = this.getPlanetSpriteKey();

    if (spriteKey && this.scene.textures.exists(spriteKey)) {
      this.planetGraphics.setVisible(false);
      if (!this.planetSprite) {
        this.planetSprite = this.scene.add.image(0, -12, spriteKey);
        this.add(this.planetSprite);
      } else if (this.planetSprite.texture.key !== spriteKey) {
        this.planetSprite.setTexture(spriteKey);
      }
      const spriteSize = displayRadius * 2;
      this.planetSprite.setDisplaySize(spriteSize, spriteSize);
      this.planetSprite.setVisible(true);
    } else {
      if (this.planetSprite) {
        this.planetSprite.setVisible(false);
      }
      this.planetGraphics.setVisible(true);

      const color = this.getOwnerColor();
      this.planetGraphics.fillStyle(color, 0.8);
      this.planetGraphics.fillCircle(0, 0, displayRadius);
      this.planetGraphics.lineStyle(2, 0xffffff, 0.5);
      this.planetGraphics.strokeCircle(0, 0, displayRadius);

      this.planetGraphics.fillStyle(color, 0.3);
      const detailScale = displayRadius / 25;
      this.planetGraphics.fillCircle(-8 * detailScale, -5 * detailScale, 8 * detailScale);
      this.planetGraphics.fillCircle(10 * detailScale, 8 * detailScale, 6 * detailScale);
    }
  }

  private drawSelectionIndicator(): void {
    this.selectionIndicator.clear();
    const color = this.getOwnerColor();
    const footprint = getPlanetFootprint(this.isHomePlanet);
    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    this.selectionIndicator.lineStyle(2, color, 0.8);
    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      this.drawHexagon(this.selectionIndicator, p.x - anchorPixel.x, p.y - anchorPixel.y, HEX_CELL_RADIUS + 2, false);
    }
  }

  public updateVisualPosition(): void {
    const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
    this.setPosition(pixel.x, pixel.y);
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  public destroy(fromScene?: boolean): void {
    if (this.ambientTween) {
      this.ambientTween.destroy();
    }
    super.destroy(fromScene);
  }

  private getOwnerColor(): number {
    if (!this.ownerId) return NEUTRAL_COLOR;
    let hash = 0;
    for (let i = 0; i < this.ownerId.length; i++) {
      hash = (hash << 5) - hash + this.ownerId.charCodeAt(i);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % PLAYER_COLORS.length;
    return PLAYER_COLORS[index];
  }

  private drawHexagon(
    graphics: Phaser.GameObjects.Graphics,
    cx: number, cy: number, size: number, fill: boolean,
  ): void {
    graphics.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const px = cx + size * Math.cos(angle);
      const py = cy + size * Math.sin(angle) * ISO_Y_SCALE;
      if (i === 0) graphics.moveTo(px, py);
      else graphics.lineTo(px, py);
    }
    graphics.closePath();
    if (fill) graphics.fillPath();
    else graphics.strokePath();
  }
}
